// src/components/AddDeviceForm.tsx
import React, { useState } from "react";
import { RegisterDeviceInput } from "../api/contracts/deviceTypes";
import { DueTimeSelector } from "./DueTimeSelector";

type AddDeviceFormProps = {
  onRegister: (device: RegisterDeviceInput) => Promise<void>;
  loading?: boolean;
};

export const AddDeviceForm: React.FC<AddDeviceFormProps> = ({
  onRegister,
  loading = false,
}) => { 
  const [step, setStep] = useState<1 | 2>(1);

  const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) =>
    `${h.toString().padStart(2, "0")}:00`
  );

  const [device, setDevice] = useState<RegisterDeviceInput>({
    deviceName: "",
    capacityLiters: 50,
    dueTimesLocal: [],
    shellyServer: "",
    shellyAuthKey: "",
    shellyDeviceId: "",
    shellyChannel: "0",

    pricing: {
      energy: {
        model: "spot",
      },

      transmission: {
        peakPrice: undefined,
        offpeakPrice: undefined,
        peakStart: "",
        offpeakStart: "",
      },
    },
  });

  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof RegisterDeviceInput>(
    key: K,
    value: RegisterDeviceInput[K]
  ) => {
    setDevice((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError(null);

    const {
      deviceName,
      shellyServer,
      shellyAuthKey,
      shellyDeviceId,
    } = device;

    if (
      !deviceName ||
      !shellyServer ||
      !shellyAuthKey ||
      !shellyDeviceId
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    await onRegister(device);

    setDevice({
      deviceName: "",
      capacityLiters: 50,
      dueTimesLocal: [],
      shellyServer: "",
      shellyAuthKey: "",
      shellyDeviceId: "",
      shellyChannel: "0",

      pricing: {
        energy: {
          model: "spot",
        },

        transmission: {
          peakPrice: undefined,
          offpeakPrice: undefined,
          peakStart: "",
          offpeakStart: "",
        },
      },
    });

    setStep(1);
  };

  const parseNumberInput = (
    value: string
  ): number | undefined => {
    if (value.trim() === "") {
      return undefined;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : undefined;
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">

      {/* STEP 1 */}
      {step === 1 && (
        <div className="space-y-6 rounded-xl border border-gray-200 bg-white p-8 shadow-sm">

          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Register new device
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Configure device identity and Shelly connection.
            </p>
          </div>

          {/* Device basics */}
          <div className="space-y-5">

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Device name
              </label>

              <input
                value={device.deviceName}
                onChange={(e) => update("deviceName", e.target.value)}
                placeholder="e.g. Bathroom boiler"
                className="
                  mt-1 w-full rounded-md border border-gray-300
                  px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Capacity
              </label>

              <select
                value={device.capacityLiters}
                onChange={(e) =>
                  update("capacityLiters", Number(e.target.value))
                }
                className="
                  mt-1 w-full rounded-md border border-gray-300
                  px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              >
                {[50, 100, 150, 200, 250, 300, 350, 400].map((c) => (
                  <option key={c} value={c}>
                    {c} L
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Shelly */}
          <div className="space-y-5 rounded-lg border border-gray-200 p-5">

            <div>
              <h3 className="text-sm font-semibold text-gray-700">
                Shelly connection
              </h3>

              <p className="text-xs text-gray-500 mt-1">
                Device cloud endpoint and authentication details.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Shelly server
              </label>

              <input
                value={device.shellyServer}
                onChange={(e) => update("shellyServer", e.target.value)}
                placeholder="https://shelly.cloud"
                className="
                  mt-1 w-full rounded-md border border-gray-300
                  px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Auth key
              </label>

              <input
                value={device.shellyAuthKey}
                onChange={(e) => update("shellyAuthKey", e.target.value)}
                placeholder="••••••••"
                className="
                  mt-1 w-full rounded-md border border-gray-300
                  px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              />
            </div>

            <div className="grid grid-cols-2 gap-4">

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Device ID
                </label>

                <input
                  value={device.shellyDeviceId}
                  onChange={(e) =>
                    update("shellyDeviceId", e.target.value)
                  }
                  className="
                    mt-1 w-full rounded-md border border-gray-300
                    px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                  "
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Channel
                </label>

                <select
                  value={device.shellyChannel}
                  onChange={(e) =>
                    update("shellyChannel", e.target.value)
                  }
                  className="
                    mt-1 w-full rounded-md border border-gray-300
                    px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                  "
                >
                  {["0", "1", "2", "3"].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="
                rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white
                hover:bg-blue-700
              "
            >
              Proceed to pricing setup
            </button>
          </div>

        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="space-y-6 rounded-xl border border-gray-200 bg-white p-8 shadow-sm">

          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Pricing setup
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Configure due times and pricing components.
            </p>
          </div>

          {/* Due times */}
          <div className="rounded-lg border border-gray-200 p-5">

            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Due times
            </h3>

            <DueTimeSelector
              dueTimes={device.dueTimesLocal}
              onChange={(times) => update("dueTimesLocal", times)}
            />
          </div>

          {/* Energy */}
          <div className="rounded-lg border border-gray-200 p-5 space-y-4">

            <div>
              <h3 className="text-sm font-semibold text-gray-700">
                Energy component
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Configure either Nord Pool spot pricing or fixed pricing.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pricing model
              </label>

              <select
                value={device.pricing.energy.model}
                onChange={(e) =>
                  update("pricing", {
                    ...device.pricing,
                    energy: {
                      ...device.pricing.energy,
                      model: e.target.value as "spot" | "fixed",
                    },
                  } as any)
                }
                className="
                  w-full rounded-md border border-gray-300
                  px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              >
                <option value="spot">Spot</option>
                <option value="fixed">Fixed</option>
              </select>
            </div>

            {/* FIXED */}
            {device.pricing.energy.model === "fixed" && (
              <div className="grid grid-cols-2 gap-3">

                <input
                  type="number"
                  step="0.01"
                  placeholder="Peak price c€/kWh"
                  value={device.pricing.energy.peakPrice ?? ""}
                  onChange={(e) =>
                    update("pricing", {
                      ...device.pricing,
                      energy: {
                        ...device.pricing.energy,
                        peakPrice: parseNumberInput(e.target.value),
                      },
                    } as any)
                  }
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                />

                <input
                  type="number"
                  step="0.01"
                  placeholder="Offpeak price c€/kWh"
                  value={device.pricing.energy.offpeakPrice ?? ""}
                  onChange={(e) =>
                    update("pricing", {
                      ...device.pricing,
                      energy: {
                        ...device.pricing.energy,
                        offpeakPrice: parseNumberInput(e.target.value),
                      },
                    } as any)
                  }
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                />

                <select
                  value={device.pricing.energy.peakStart ?? ""}
                  onChange={(e) =>
                    update("pricing", {
                      ...device.pricing,
                      energy: {
                        ...device.pricing.energy,
                        peakStart: e.target.value,
                      },
                    } as any)
                  }
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm
                            focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Peak start</option>

                  {HOUR_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>

                <select
                  value={device.pricing.energy.offpeakStart ?? ""}
                  onChange={(e) =>
                    update("pricing", {
                      ...device.pricing,
                      energy: {
                        ...device.pricing.energy,
                        offpeakStart: e.target.value,
                      },
                    } as any)
                  }
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm
                            focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Offpeak start</option>

                  {HOUR_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>

              </div>
            )}

            {/* SPOT */}
            {device.pricing.energy.model === "spot" && (
              <div>

                <input
                  type="number"
                  step="0.01"
                  placeholder="Markup c€/kWh"
                  value={(device.pricing.energy as any).markup ?? ""}
                  onChange={(e) =>
                    update("pricing", {
                      ...device.pricing,
                      energy: {
                        ...device.pricing.energy,
                        markup: parseNumberInput(e.target.value),
                      },
                    } as any)
                  }
                  className="
                    w-full rounded-md border border-gray-300
                    px-3 py-2 text-sm
                  "
                />
              </div>
            )}

          </div>

          {/* Transmission */}
          <div className="rounded-lg border border-gray-200 p-5 space-y-4">

            <div>
              <h3 className="text-sm font-semibold text-gray-700">
                Transmission component
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Configure regional transmission pricing.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">

              <input
                type="number"
                step="0.01"
                placeholder="Peak price c€/kWh"
                value={device.pricing.transmission.peakPrice ?? ""}
                onChange={(e) =>
                  update("pricing", {
                    ...device.pricing,
                    transmission: {
                      ...device.pricing.transmission,
                      peakPrice: parseNumberInput(e.target.value),
                    },
                  } as any)
                }
                className="border border-gray-300 rounded-md px-3 py-2 text-sm
                          focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <input
                type="number"
                step="0.01"
                placeholder="Offpeak price c€/kWh"
                value={device.pricing.transmission.offpeakPrice ?? ""}
                onChange={(e) =>
                  update("pricing", {
                    ...device.pricing,
                    transmission: {
                      ...device.pricing.transmission,
                      offpeakPrice: parseNumberInput(e.target.value),
                    },
                  } as any)
                }
                className="border border-gray-300 rounded-md px-3 py-2 text-sm
                          focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <select
                value={device.pricing.transmission.peakStart ?? ""}
                onChange={(e) =>
                  update("pricing", {
                    ...device.pricing,
                    transmission: {
                      ...device.pricing.transmission,
                      peakStart: e.target.value,
                    },
                  } as any)
                }
                className="border border-gray-300 rounded-md px-3 py-2 text-sm
                          focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Peak start</option>

                {HOUR_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>

              <select
                value={device.pricing.transmission.offpeakStart ?? ""}
                onChange={(e) =>
                  update("pricing", {
                    ...device.pricing,
                    transmission: {
                      ...device.pricing.transmission,
                      offpeakStart: e.target.value,
                    },
                  } as any)
                }
                className="border border-gray-300 rounded-md px-3 py-2 text-sm
                          focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Offpeak start</option>

                {HOUR_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>

            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600">
              {error}
            </p>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between pt-2">

            <button
              type="button"
              onClick={() => setStep(1)}
              className="
                rounded-md border border-gray-300
                px-4 py-2 text-sm text-gray-700
                hover:bg-gray-50
              "
            >
              Back
            </button>

            <button
              type="submit"
              disabled={loading}
              className="
                rounded-md bg-blue-600
                px-5 py-2 text-sm font-medium text-white
                hover:bg-blue-700
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              {loading ? "Registering…" : "Register device"}
            </button>

          </div>

        </div>
      )}

    </form>
  );
};